// Admin KB CMS — list, create, edit, delete knowledge articles.
import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, BookOpen, Search, Eye, EyeOff } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const CATEGORIES = ["billing", "fulfillment", "service", "safety", "privacy", "technical", "other"];
const SUBTYPES = [
    "service_quality", "product_defect", "delayed_delivery", "wrong_item",
    "billing_dispute", "refund_delay", "doctor_consult", "lab_test",
    "privacy", "safety", "other",
];

const empty = { slug: "", title: "", category: "fulfillment", subtype: "service_quality", body: "", tags: [], active: true };

const AdminKbContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | empty | item
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/kb-articles");
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = items.filter((a) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (a.title || "").toLowerCase().includes(q)
            || (a.slug || "").toLowerCase().includes(q)
            || (a.tags || []).some((t) => t.toLowerCase().includes(q));
    });

    const startCreate = () => { setEditing({ ...empty, _isNew: true }); setShowPreview(false); };
    const startEdit = (item) => { setEditing({ ...item, _isNew: false }); setShowPreview(false); };

    const save = async () => {
        const f = editing;
        if (!f) return;
        if (f.slug.length < 2 || f.title.length < 2 || f.body.length < 10) {
            toast.error("Slug, title, and body are all required.");
            return;
        }
        const payload = {
            slug: f.slug.trim(),
            title: f.title.trim(),
            category: f.category,
            subtype: f.subtype,
            body: f.body,
            tags: f.tags,
            active: f.active,
        };
        try {
            setSaving(true);
            if (f._isNew) {
                await api.post("/admin/kb-articles", payload);
                toast.success("Article created");
            } else {
                await api.patch(`/admin/kb-articles/${f.id}`, payload);
                toast.success("Article updated");
            }
            setEditing(null);
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (item) => {
        if (!window.confirm(`Delete article "${item.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/kb-articles/${item.id}`);
            toast.success("Article deleted");
            await load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-kb-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="text-sm text-zinc-500">
                        {items.length} articles total — these power AI complaint triage suggestions and the public /help/kb browser.
                    </p>
                </div>
                <button onClick={startCreate} className="btn-brand" data-testid="admin-kb-new-btn">
                    <Plus className="h-4 w-4" /> New article
                </button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by title, slug, or tag…"
                    className="w-full rounded-lg border border-zinc-200 pl-8 pr-3 py-2 text-sm outline-none focus:border-brand-500"
                    data-testid="admin-kb-search"
                />
            </div>

            {/* List */}
            <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-zinc-100" data-testid="admin-kb-table">
                    <thead className="bg-zinc-50/60 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr>
                            <th className="px-4 py-3">Title</th>
                            <th className="px-4 py-3">Slug</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Subtype</th>
                            <th className="px-4 py-3">Helpful</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-sm">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">No articles.</td></tr>
                        ) : (
                            filtered.map((a) => (
                                <tr key={a.id} className="hover:bg-zinc-50/50" data-testid={`row-kb-${a.slug}`}>
                                    <td className="px-4 py-3"><p className="line-clamp-1 font-medium text-zinc-900">{a.title}</p></td>
                                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{a.slug}</td>
                                    <td className="px-4 py-3 text-xs text-zinc-600">{a.category}</td>
                                    <td className="px-4 py-3 text-xs text-zinc-600">{(a.subtype || "").replace(/_/g, " ")}</td>
                                    <td className="px-4 py-3 text-xs">
                                        <span className="text-emerald-700">👍 {a.helpful_count || 0}</span>{" "}
                                        <span className="text-rose-600">👎 {a.not_helpful_count || 0}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {a.active === false ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">
                                                <EyeOff className="h-3 w-3" /> Inactive
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                <Eye className="h-3 w-3" /> Active
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => startEdit(a)}
                                                className="rounded-full p-1.5 text-zinc-500 hover:bg-brand-50 hover:text-brand-700"
                                                aria-label="Edit"
                                                data-testid={`kb-edit-${a.slug}`}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() => del(a)}
                                                className="rounded-full p-1.5 text-zinc-500 hover:bg-rose-50 hover:text-rose-700"
                                                aria-label="Delete"
                                                data-testid={`kb-delete-${a.slug}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="admin-kb-modal">
                    <div className="flex h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                        <header className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">{editing._isNew ? "Create" : "Edit"} article</p>
                                <h3 className="text-base font-semibold text-zinc-900">{editing.title || "Untitled"}</h3>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setShowPreview((p) => !p)}
                                    className={`rounded-md border px-2.5 py-1 text-xs font-medium ${showPreview ? "border-brand-600 bg-brand-50 text-brand-700" : "border-zinc-200 text-zinc-700 hover:border-brand-300"}`}
                                    data-testid="kb-preview-toggle"
                                >
                                    {showPreview ? "Hide preview" : "Show preview"}
                                </button>
                                <button onClick={() => setEditing(null)} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-100" aria-label="Close" data-testid="kb-modal-close">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </header>

                        <div className="flex flex-1 overflow-hidden">
                            {/* Form */}
                            <div className={`overflow-y-auto px-6 py-5 ${showPreview ? "w-1/2 border-r border-zinc-100" : "w-full"}`}>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Slug *">
                                        <input
                                            value={editing.slug}
                                            onChange={(e) => setEditing((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))}
                                            disabled={!editing._isNew}
                                            placeholder="kebab-case-slug"
                                            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono outline-none focus:border-brand-500 disabled:bg-zinc-50"
                                            data-testid="kb-input-slug"
                                        />
                                    </Field>
                                    <Field label="Title *">
                                        <input
                                            value={editing.title}
                                            onChange={(e) => setEditing((f) => ({ ...f, title: e.target.value }))}
                                            className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                                            data-testid="kb-input-title"
                                        />
                                    </Field>
                                    <Field label="Category">
                                        <select
                                            value={editing.category}
                                            onChange={(e) => setEditing((f) => ({ ...f, category: e.target.value }))}
                                            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                                            data-testid="kb-input-category"
                                        >
                                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Subtype">
                                        <select
                                            value={editing.subtype}
                                            onChange={(e) => setEditing((f) => ({ ...f, subtype: e.target.value }))}
                                            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                                            data-testid="kb-input-subtype"
                                        >
                                            {SUBTYPES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Tags (comma-separated)">
                                    <input
                                        value={(editing.tags || []).join(", ")}
                                        onChange={(e) => setEditing((f) => ({ ...f, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) }))}
                                        className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                                        data-testid="kb-input-tags"
                                    />
                                </Field>
                                <Field label="Body (Markdown supported) *">
                                    <textarea
                                        value={editing.body}
                                        onChange={(e) => setEditing((f) => ({ ...f, body: e.target.value }))}
                                        rows={14}
                                        className="w-full resize-none rounded-lg border border-zinc-200 p-3 text-sm font-mono outline-none focus:border-brand-500"
                                        data-testid="kb-input-body"
                                    />
                                </Field>
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={editing.active}
                                        onChange={(e) => setEditing((f) => ({ ...f, active: e.target.checked }))}
                                        className="h-4 w-4 rounded border-zinc-300 text-brand-600"
                                        data-testid="kb-input-active"
                                    />
                                    Active (visible to AI triage + public KB)
                                </label>
                            </div>

                            {/* Preview */}
                            {showPreview && (
                                <div className="w-1/2 overflow-y-auto bg-zinc-50/60 px-6 py-5" data-testid="kb-preview">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">Preview</p>
                                    <h4 className="mt-1 text-lg font-semibold text-zinc-900">{editing.title || "Untitled"}</h4>
                                    <div className="prose prose-sm mt-3 max-w-none">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{editing.body || "*Body will render here.*"}</ReactMarkdown>
                                    </div>
                                </div>
                            )}
                        </div>

                        <footer className="flex items-center justify-end gap-2 border-t border-zinc-100 px-6 py-4">
                            <button onClick={() => setEditing(null)} className="rounded-full border border-zinc-200 px-4 py-2 text-sm font-medium hover:border-zinc-300">Cancel</button>
                            <button
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                                data-testid="kb-modal-save"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                {editing._isNew ? "Create" : "Save changes"}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="mt-3 block">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

const AdminKb = () => (
    <AdminAuthGuard>
        <AdminLayout title="Knowledge Base" subtitle="Articles served to customers + AI triage suggestions">
            <AdminKbContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminKb;
